import React, { type ReactNode } from "react";
import { formatDistanceToNow, max } from "date-fns";
import { createClasses } from "@bloomberg/media-ui-core";

import { MARKETS_WRAP_NICODE } from "~constants";
import type { DropdownLabel } from "~types/lineup";
import type { LiveBlogState } from "~types/types";

import styles from "./OptionalEyebrow.module.css";

export type PresetLabel =
    | DropdownLabel
    | "opinion"
    | "opinionRelated"
    | "listen"
    | "watch"
    | "closed"
    | "relativeTimestamp";

export type LiveBlogStatus =
    | Extract<LiveBlogState, "live" | "closed">
    | "comingUp";

const BIG_TAKE_LABEL = "the big take";
const BIG_TAKE_FRANCHISE = "the-big-take";
const LIVE_BLOG_STATE_LABELS = new Set<LiveBlogStatus>([
    "live",
    "closed",
    "comingUp",
]);
export const AUTHOR_EYEBROW = "author";

type RenderablePresetLabel = Exclude<PresetLabel, "noLabel">;

export const PRESET_LABELS: Record<RenderablePresetLabel, ReactNode> = {
    analysis: "Analysis",
    author: "Author",
    bigTake: "The Big Take",
    breaking: "Breaking",
    breakingTime: "Breaking",
    closed: "Live Blog Recap",
    comingUp: "Coming Up",
    exclusive: "Exclusive",
    explainer: "Explainer",
    howTo: "How To",
    listen: "Listen",
    live: "Live",
    marketsWrap: "Markets Wrap",
    opinion: "Bloomberg Opinion",
    opinionRelated: "Opinion",
    relativeTimestamp: "Relative Timestamp",
    watch: "Watch",
};

type OptionalEyebrowProps = {
    textLabel?: string;
    presetLabel?: PresetLabel;
    label?: string;
    updatedAt?: string;
    publishedAt?: string;
    authorName?: string;
    authorRole?: string;
    className?: string;
    franchise?: string;
    liveBlogState?: LiveBlogStatus;
    nicodes?: string[];
};

function getTextLabelToUse({
    label,
    franchise,
    textLabel,
    hasExplicitlyPassedLabels,
    nicodes,
}: Pick<
    OptionalEyebrowProps,
    "label" | "franchise" | "textLabel" | "nicodes"
> & {
    hasExplicitlyPassedLabels: boolean;
}) {
    const isBigTake =
        label?.trim()?.toLocaleLowerCase() === BIG_TAKE_LABEL ||
        franchise === BIG_TAKE_FRANCHISE;

    if (isBigTake && !hasExplicitlyPassedLabels) {
        return PRESET_LABELS.bigTake;
    }

    if (nicodes) {
        const nicodesSet = new Set(nicodes);
        if (nicodesSet.has(MARKETS_WRAP_NICODE)) {
            return PRESET_LABELS.marketsWrap;
        }
    }

    if (textLabel === "explainer" || textLabel === "howTo") {
        return PRESET_LABELS[textLabel];
    }

    return textLabel;
}

function getPresetLabelToUse({
    liveBlogState,
    presetLabel,
}: Pick<OptionalEyebrowProps, "liveBlogState" | "presetLabel">) {
    // note(calbachten): design requested presetLabels override liveblog state
    // so comingUp could be applied even if LiveBlogStatus logic would
    // not produce a comingUp label
    if (
        LIVE_BLOG_STATE_LABELS.has(liveBlogState as LiveBlogStatus) &&
        !presetLabel
    ) {
        return liveBlogState;
    }

    return presetLabel;
}

function getLastEditDate(timestamps: (string | undefined)[]) {
    const dates = (timestamps.filter(Boolean) as string[]).map(
        (timestamp) => new Date(timestamp)
    );

    if (!dates.length) {
        return null;
    }

    return max(dates);
}

export default function OptionalEyebrow({
    textLabel,
    presetLabel,
    updatedAt,
    publishedAt,
    authorName,
    authorRole,
    className,
    label,
    franchise,
    liveBlogState,
    nicodes,
}: OptionalEyebrowProps) {
    const latestDate = getLastEditDate([updatedAt, publishedAt]);
    const isTimestamped = Boolean(latestDate);
    const timestamp = latestDate?.toISOString();
    const timeFromPublication =
        isTimestamped &&
        formatDistanceToNow(latestDate as Date, {
            addSuffix: true,
        });

    const presetLabelToUse = getPresetLabelToUse({
        liveBlogState,
        presetLabel,
    });
    const isRelatedOpinion = presetLabel === "opinionRelated";
    const hasNoLabel = presetLabel === "noLabel";
    const hasExplicitlyPassedLabels = Boolean(textLabel || presetLabel);
    const shouldHaveTimestamp = presetLabel === "breakingTime" && isTimestamped;
    const shouldHaveByline = isRelatedOpinion && Boolean(authorName);

    const textLabelToUse = getTextLabelToUse({
        label,
        franchise,
        textLabel,
        nicodes,
        hasExplicitlyPassedLabels,
    });

    if (
        hasNoLabel ||
        (!hasExplicitlyPassedLabels && !textLabelToUse && !presetLabelToUse)
    ) {
        return null;
    }

    if (presetLabel === "listen" || presetLabel === "watch") {
        return (
            <div
                data-component="optional-eyebrow"
                className={createClasses(styles.optionalEyebrow, className)}
            >
                {presetLabel && PRESET_LABELS[presetLabel]}

                {textLabel && (
                    <span className={styles.duration}>&nbsp;({textLabel})</span>
                )}
            </div>
        );
    }

    if (presetLabel === "relativeTimestamp" && isTimestamped) {
        return (
            <time
                dateTime={timestamp}
                className={styles.timeStampAlone}
                data-component="optional-eyebrow"
                suppressHydrationWarning
            >
                {timeFromPublication}
            </time>
        );
    }

    if (presetLabel === AUTHOR_EYEBROW && authorName) {
        return (
            <div
                data-component="optional-eyebrow"
                className={createClasses(
                    styles.optionalEyebrow,
                    presetLabelToUse && styles[presetLabelToUse],
                    className
                )}
            >
                <span className={styles.authorName}>
                    {authorName}
                    {authorRole ? ` | ${authorRole}` : ""}
                </span>
            </div>
        );
    }

    return (
        <div
            data-component="optional-eyebrow"
            className={createClasses(
                styles.optionalEyebrow,
                presetLabelToUse && styles[presetLabelToUse],
                className
            )}
        >
            {textLabelToUse ||
                PRESET_LABELS[presetLabelToUse as RenderablePresetLabel]}
            {shouldHaveTimestamp && (
                <time
                    dateTime={timestamp}
                    className={styles.relativeTimestamp}
                    suppressHydrationWarning
                >
                    {timeFromPublication}
                </time>
            )}
            {shouldHaveByline && (
                <span className={styles.authorName}>{authorName}</span>
            )}
        </div>
    );
}
